<template>
  <div>
    <AppSection top class="check-status">
      <AppContainer>
        <h1 class="mb-7">
          {{ $tt('status_page.visa_status') }}
        </h1>
        <AppThirdLayout>
          <AppImg src="/images/status/hourglass.jpg" :alt="$tt('hourglass')" />
          <div>
            <h4>{{ $tt('status_page.intro') }}</h4>
            <div class="status-container pt-7 flex gap-7">
              <div class="shadow border-md pa-6 equal">
                <v-form :disabled="submitted" :ref="setFormRef" @submit.prevent="sendStatus">
                  <div class="flex col gap-3 mb-2">
                    <FormHeading :h="4">
                      {{ $tt('status_page.instructions_title') }}
                    </FormHeading>
                    <EmailField  v-model="form.email" :label="$tt('status_page.email_address')" />
                    <TextField
                      v-model="form.orderId"
                      :rules="[required]"
                      :label="$tt('application_id')"
                      append-inner-icon="mdi:ticket-confirmation-outline"
                    />
                  </div>
                  <AppBtn
                    type="submit"
                    size="large"
                    prepend-icon="mdi:progress-check"
                    primary block
                    :disabled="!valid"
                    :loading="loading"
                  >
                    {{ $tt('status_page.check') }}
                  </AppBtn>
                </v-form>
              </div>
              <div class="equal">
                <p class="lead">
                  <strong>
                    {{ $tt('status_page.find_application_id_title') }}
                  </strong>
                </p>
                <p>
                  {{ $tt('status_page.find_application_id') }}
                </p>
              </div>
            </div>
          </div>
        </AppThirdLayout>         
      </AppContainer>
    </AppSection>

    <AppSection class="find">
      <AppContainer>
        <AppThirdLayout>
          <h3 class="mb-8" v-html="$tt('status_page.find_here_title')" />
          <p class="lead">{{ $tt('status_page.find_here_body') }}</p>
        </AppThirdLayout>
        <FeatureTriad :items carousel />
      </AppContainer>
    </AppSection>

    <AppSection class="steps">
      <StripHeader>{{ $tt('status_page.why_title') }}</StripHeader>
      <StepsSection view="status" class="steps pb-0" titleless top />
    </AppSection>

    <AppSection class="help">
      <AppContainer>
        <h2 v-html="$tt('status_page.need_help_title')" class="mb-8" />
        <AppThirdLayout>
          <AppImg src="/images/status/a-man-lays-with-his-head-on-the-laptop.jpg" :alt="$tt('status_page.man_lays_on_laptop_img')" />
          <div class="help-content flex gap-8">
            <p class="lead equal">
              <strong>{{ $tt('status_page.need_help_body') }}</strong>
            </p>
            <div class="ctas flex col gap-7 equal">
              <h4>{{ $tt('status_page.contact_us') }}</h4>
              <div class="flex gap-4 justify-between wrap">
                <SupportEmailLink class="plain contact-link">
                  <AppIcon color="primary" icon="clarity:email-line" size="x-large" />
                </SupportEmailLink>
                <PhoneLink class="plain contact-link">
                  <AppIcon color="primary" icon="uil:phone" size="x-large" />
                </PhoneLink>
                <a v-if="telegram" :href="telegram" target="_blank" class="plain contact-link">
                  <AppIcon color="primary" icon="uil:telegram-alt" size="x-large" />
                </a>
                <WhatsappLink class="plain contact-link">
                  <AppIcon color="primary" icon="mdi:whatsapp" size="x-large" />
                </WhatsappLink>
              </div>
              <AppBtn primary block @click="$router.push('/contact')">
                {{ $tt('send_message') }}
              </AppBtn>
            </div>
          </div>
        </AppThirdLayout>
      </AppContainer>
    </AppSection>
  </div>
</template>

<script setup lang="ts">
import { sendOrderStatus } from '~/helpers/http';
import type { SendOrderStatusReqBody } from '~/types';

const { telegram } = useCommunication();
const { required } = useValidation();
const { setFormRef, submit, loading, submitted, valid } = useForm();
const { tt } = useLocalization();

usePageHead({
  title: tt('status_page.title'),
  description: tt('status_page.meta_desc'),
  alternateBranches: 'all'
});

const form = reactive<SendOrderStatusReqBody>({
  email: '',
  orderId: ''
});

function sendStatus() {
  submit(async () => {
    const { email, orderId } = form;
    const res = await sendOrderStatus({ email, orderId }).catch(() => null);
    if (!res?.success) throw new Error('status_page.not_found');
  }, { successMsg: 'status_page.success' });
}

const items = [
  {
    src: '/images/status/clock.jpg',
    alt: tt('status_page.clock_img'),
    title: tt('status_page.find_here_1'),
  },
  {
    src: '/images/about/docement-with-check-mark-icon.jpg',
    alt: tt('about_page.img_doc_with_checkmark'),
    title: tt('status_page.find_here_2'),
  },
  {
    src: '/images/status/progress-bar-on-travel-card.jpg',
    alt: tt('status_page.progress_bar_on_card_img'),
    title: tt('status_page.find_here_3'),
  }
];
</script>

<style scoped lang="scss">
.lead {
  font-size: 20px;
}
.find h3 {
  font-size: 36px;
}

.contact-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(var(--v-theme-tertiary)) !important;
  border-radius: 50%;
  padding: var(--space-3);
}

@media (max-width: 850px) {
  h1 {
    font-size: 30px;
  }
  p {
    font-size: 14px;
  }
  .lead {
    font-size: 16px;
  }
  .layout {
    gap: var(--space-3) !important;
  }
  .check-status {
    padding-top: var(--space-6) !important;
    text-align: center;
    img {
      align-self: center;
      max-width: 130px;
    }
    h4 {
      font-size: 18px;
      margin-bottom: 0;
    }
    .status-container {
      flex-direction: column;
      padding-top: var(--space-7) !important;
    }
  }
  .find {
    h3 {
      font-size: 24px;
      margin-bottom: 0 !important;
    }
    img {
      max-width: 180px;
    }
  }
  .why {
    .steps {
      padding-top: 40% !important;
    }
    :deep(figure>img) {
      position: absolute;
      margin-inline: auto;
      width: 300px;
      top: -300px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }
  }
  .steps {
    padding-bottom: 0 !important;
  }
  .help {
    padding-top: var(--space-8);
    padding-bottom: var(--space-9);
    .help-content {
      flex-direction: column;
      .ctas {
        align-items: center;
      }
      p {
        font-size: 18px;
      }
      h3 {
        text-align: center;
        margin-bottom: 0;
      }
      button {
        width: fit-content !important;
      }
    }
  }
}
</style>